import { api } from "../api/client.ts";
import type {
  Brief,
  Conversation,
  Finding,
  Issue,
  PageIssue,
  PageReviewOut,
  ReviewDetail,
  SpaceOut,
} from "../api/types.gen.ts";
import { SLOT, t } from "../i18n/index.ts";
import { commentCount } from "../lib/commentCounts.ts";
import { useConversation } from "../lib/conversation.ts";
import { formatInstant, formatRelative } from "../lib/format.ts";
import { spaceApi, spaceUrl } from "../lib/paths.ts";
import { reviewerName, usePeople } from "../lib/people.ts";
import { type Resource, useResource } from "../lib/resource.ts";
import { canEdit } from "../lib/roles.ts";
import { tabHref, tabParam } from "../lib/router.ts";
import type { PageProps } from "../lib/state.ts";
import { titleOf } from "../lib/titles.ts";
import { Person } from "../ui/Avatar.tsx";
import { Link } from "../ui/Button.tsx";
import { CommentCount, SeverityLabel } from "../ui/counts.tsx";
import { Async, Badge, Freshness, Section, Slotted } from "../ui/misc.tsx";
import { PageHeader } from "../ui/PageHeader.tsx";
import { LINK } from "../ui/styles.ts";
import { TabNav } from "../ui/TabNav.tsx";
import { ConversationLabel } from "./comments/conversations.tsx";
import { RichText } from "./comments/richText.tsx";
import { InlineThread, Thread } from "./comments/thread.tsx";
import { FileRow } from "./item/attachments.tsx";
import { FindingActions } from "./item/findingActions.tsx";

/** How many of the reviewer's reviews of the item to list. */
const SERIES = 20;

interface Triage {
  space: SpaceOut;
  item: string;
  /** The item's open findings by id (closed ones aren't acted on), with their severity now. */
  open: ReadonlyMap<string, Issue>;
  onChange: () => void;
}

/** A review's findings' threads: where, and the item's conversation. */
interface Talk {
  item: string;
  review: string;
  conv: Resource<Conversation>;
}

interface FindingsProps {
  space: SpaceOut;
  list: readonly Finding[];
  added?: ReadonlySet<string>;
  gone?: boolean;
  triage?: Triage | undefined;
  /** The item's conversation: each finding's thread opens in place. */
  talk?: Talk | undefined;
}

function FindingRow({ space, f, added, gone, triage, talk }: Omit<FindingsProps, "list"> & { f: Finding }) {
  const live = triage && !gone && triage.open.has(f.id) && canEdit(triage.space);
  const inline = {
    space,
    topic: { item: talk?.item ?? "" },
    conv: gone ? undefined : talk?.conv,
    at: { review: talk?.review, finding: f.id },
    close: live && triage ? { item: triage.item, finding: f.id, asOf: undefined, onChange: triage.onChange } : null,
  };
  return (
    <InlineThread {...inline}>
      {(toggle, thread) => (
        <li
          class={`flex flex-col gap-1 rounded-md border border-zinc-200 p-3 text-sm ${gone ? "bg-zinc-50 text-zinc-500" : "bg-white"}`}
        >
          <span class="flex flex-wrap items-center gap-2">
            <SeverityLabel severity={f.severity} gone={gone ?? false} />
            {f.path ? <code class="text-xs text-zinc-500">{f.path}</code> : null}
            {added?.has(f.id) ? <Badge tone="blue">{t("review.new")}</Badge> : null}
            {triage && !gone ? <FindingState f={f} triage={triage} /> : null}
          </span>
          <RichText space={space} text={f.message} inline={true} class={gone ? "line-through" : "text-zinc-800"} />
          {toggle ? <span class="flex justify-end">{toggle}</span> : null}
          {thread ? <div class="mt-2">{thread}</div> : null}
        </li>
      )}
    </InlineThread>
  );
}

function Findings({ list, ...p }: FindingsProps) {
  return (
    <ul class="flex flex-col gap-2">
      {list.map((f) => (
        <FindingRow key={f.id} f={f} {...p} />
      ))}
    </ul>
  );
}

/** Where a finding stands now: closed, or open (adjusted, maybe) with what can be done to it. */
function FindingState({ f, triage }: { f: Finding; triage: Triage }) {
  const now = triage.open.get(f.id);
  if (!now) {
    return <Badge tone="gray">{t("review.closedNow")}</Badge>;
  }
  return (
    <>
      {now.severity === f.severity ? null : (
        <span class="text-xs text-zinc-500">
          {t("review.nowSeverity", { severity: t(`severity.${now.severity}`) })}
        </span>
      )}
      {canEdit(triage.space) ? (
        <span class="ml-auto">
          <FindingActions space={triage.space} item={triage.item} issue={now} onChange={triage.onChange} />
        </span>
      ) : null}
    </>
  );
}

/** An item (or, for `review: true`, a review of one) the review relied on. */
function Dep({ space, b }: { space: SpaceOut; b: Brief }) {
  if (b.hidden) {
    return <span class="text-zinc-500 italic">{t("item.privateItem")}</span>;
  }
  if (b.missing) {
    return <span class="text-zinc-500 line-through">{b.id}</span>;
  }
  const title = titleOf(b);
  return b.review ? (
    <Link href={spaceUrl(space, `/reviews/${b.id}`)} class={LINK}>
      {t("review.of", { label: title })}
    </Link>
  ) : (
    <Link href={spaceUrl(space, `/items/${b.id}`)} class={LINK}>
      {title}
    </Link>
  );
}

/** The reviewer's reviews of the same item, newest first, this one marked. */
function Series({ space, d, name }: { space: SpaceOut; d: ReviewDetail; name: string }) {
  const list = useResource(`reviews:${space.id}:${d.item}:${d.reviewer}`, (signal) =>
    api.get<PageReviewOut>(spaceApi(space, "/reviews"), { item: d.item, reviewer: d.reviewer, limit: SERIES }, signal),
  );
  return (
    <Section title={t("review.series", { name })}>
      <Async r={list}>
        {(page) => (
          <ol class="flex flex-col gap-1 text-sm">
            {page.data.map((r) => (
              <li key={r.id} class="flex flex-wrap items-center gap-2">
                <Link
                  href={spaceUrl(space, `/reviews/${r.id}`)}
                  class={`${LINK} aria-[current=page]:text-zinc-900 aria-[current=page]:no-underline`}
                  aria-current={r.id === d.id ? "page" : undefined}
                  title={formatInstant(r.created_at)}
                >
                  {formatRelative(r.created_at)}
                </Link>
                <span class="text-zinc-600">{t("item.findings", { count: r.issues?.length ?? 0 })}</span>
                <CommentCount
                  count={commentCount(space.id, "reviews", r.id)}
                  href={spaceUrl(space, `/reviews/${r.id}?tab=conversation`)}
                />
                <Freshness stale={r.stale_at} />
              </li>
            ))}
          </ol>
        )}
      </Async>
    </Section>
  );
}

function ReviewView({ space, d, onChange }: { space: SpaceOut; d: ReviewDetail; onChange: () => void }) {
  const open = useResource(`issues:${space.id}:item:${d.item}`, (signal) =>
    api.get<PageIssue>(spaceApi(space, "/issues"), { item: d.item, limit: 200 }, signal),
  );
  const triage: Triage = {
    space,
    item: d.item,
    open: new Map((open.data?.data ?? []).flatMap((i) => (i.id ? [[i.id, i]] : []))),
    onChange: () => {
      open.reload();
      onChange();
    },
  };
  const { item } = d.links;
  const issues = d.issues ?? [];
  const gone = d.removed ?? [];
  const closed = d.resolve?.length ?? 0;
  const members = usePeople();
  const name = reviewerName(members, d);
  const conv = useConversation(space, { item: d.item });
  const talk: Talk = { item: d.item, review: d.id, conv };
  const tab = tabParam(["review", "conversation"]);
  const base = spaceUrl(space, `/reviews/${d.id}`);
  // Attachments since deleted aren't links to anything.
  const files = d.links.attachments.filter((b) => !b.missing);
  return (
    <>
      <PageHeader
        id={d.id}
        title={
          <Slotted text={t("review.of", { label: SLOT })}>
            <Link href={spaceUrl(space, `/items/${item.id}`)} class={LINK}>
              {titleOf(item)}
            </Link>
          </Slotted>
        }
      />
      <p class="-mt-4 mb-6 flex flex-wrap items-center gap-3 text-sm text-zinc-600">
        <span class="font-medium text-zinc-900">
          <Person name={name} id={d.reviewer} size={24} />
        </span>
        <span title={formatInstant(d.created_at)}>{formatRelative(d.created_at)}</span>
        <Freshness stale={d.stale_at} />
        <Link href={spaceUrl(space, `/history/${d.ver}`)} class={LINK}>
          {t("review.change")}
        </Link>
      </p>
      <TabNav
        label={t("review.tabs")}
        tabs={[
          { href: tabHref(base, null), label: t("review.tab.review"), current: tab === "review" },
          {
            href: tabHref(base, "conversation"),
            label: <ConversationLabel conv={conv} at={{ review: d.id }} />,
            current: tab === "conversation",
          },
        ]}
      />
      {tab === "conversation" ? (
        <div class="max-w-3xl">
          <Thread space={space} topic={{ item: d.item }} conv={conv} at={{ review: d.id }} empty={t("comments.none")} />
        </div>
      ) : null}
      <div
        class={`grid grid-cols-1 gap-8 @3xl/main:grid-cols-[minmax(0,1fr)_22rem] ${tab === "review" ? "" : "hidden"}`}
      >
        <div>
          {d.message ? <RichText space={space} text={d.message} class="mb-8 text-zinc-800" /> : null}
          <Section title={t("item.findings", { count: issues.length })}>
            {issues.length > 0 ? (
              <Findings
                space={space}
                list={issues}
                added={new Set(d.added ?? [])}
                triage={open.data ? triage : undefined}
                talk={talk}
              />
            ) : null}
          </Section>
          {gone.length > 0 ? (
            <Section title={t("review.gone")}>
              <Findings space={space} list={gone} gone={true} />
            </Section>
          ) : null}
          {closed > 0 ? <p class="mb-8 text-sm text-zinc-600">{t("review.closed", { count: closed })}</p> : null}
        </div>
        <aside>
          {d.links.deps.length > 0 ? (
            <Section title={t("review.reliedOn")}>
              <ul class="flex flex-col gap-1 text-sm">
                {d.links.deps.map((b) => (
                  <li key={b.id}>
                    <Dep space={space} b={b} />
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}
          {files.length > 0 ? (
            <Section title={t("attachments.title")}>
              <ul class="divide-y divide-zinc-100">
                {files.map((b) => (
                  <FileRow key={b.id} space={space} b={b} />
                ))}
              </ul>
            </Section>
          ) : null}
          <Series space={space} d={d} name={name} />
        </aside>
      </div>
    </>
  );
}

/** A review on its own: what it found, what changed since the reviewer's previous one, and what it relied on. */
export function Review({ space, params }: PageProps) {
  const id = params["id"] ?? "";
  const r = useResource(`review:${space.id}:${id}`, (signal) =>
    api.get<ReviewDetail>(spaceApi(space, `/reviews/${id}`), undefined, signal),
  );
  return <Async r={r}>{(d) => <ReviewView space={space} d={d} onChange={r.reload} />}</Async>;
}
